import { useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import type { Scope } from '@cef-ai/vault-sdk';
import { AgentAvatar, IconButton, Menu, MenuItem } from '@cere/cere-design-system';
import { themeForScope } from './scope-theming';

export interface ScopeChipAgent {
  agentId: string;
  name: string;
}

interface Props {
  scope: Scope;
  agents: ScopeChipAgent[];
  onEdit: () => void;
  onDelete: () => void;
}

const CHIP_CAP = 4;

export function ScopeCard({ scope, agents, onEdit, onDelete }: Props) {
  const theme = themeForScope(scope.name);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const label = scope.displayName ?? scope.name;
  const initial = (label[0] ?? scope.name[0] ?? '?').toUpperCase();
  const isDefault = scope.name === 'default';
  const shown = agents.slice(0, CHIP_CAP);
  const more = agents.length - shown.length;

  return (
    <div className="vault-scopes__card" style={{ borderLeft: `4px solid ${theme.accentVar}` }}>
      <div className="vault-scopes__card-head">
        <span className="vault-scopes__card-avatar" style={{ background: theme.accentVar }}>{initial}</span>
        <IconButton
          aria-label="Scope actions"
          size="small"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => setAnchor(e.currentTarget)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchor} onClose={() => setAnchor(null)}>
          <MenuItem label="Edit" onClick={() => { setAnchor(null); onEdit(); }} />
          <MenuItem label="Delete" onClick={() => { setAnchor(null); onDelete(); }} />
        </Menu>
      </div>

      <div className="vault-scopes__card-label-row">
        <span className="vault-scopes__card-label">{label}</span>
        {isDefault && <span className="vault-scopes__badge">Default</span>}
      </div>
      <div className="vault-scopes__card-name">{scope.name} · immutable</div>

      {agents.length === 0 ? (
        <div className="vault-scopes__empty-agents">No agents attached</div>
      ) : (
        <div className="vault-scopes__chips">
          {shown.map((a) => (
            <span key={a.agentId} className="vault-scopes__chip">
              <AgentAvatar seed={a.agentId} name={a.name} size={16} />
              <span className="vault-scopes__chip-name">{a.name}</span>
            </span>
          ))}
          {more > 0 && <span className="vault-scopes__chip vault-scopes__chip--more">+{more} more</span>}
        </div>
      )}
    </div>
  );
}
